/**
 * Messaging hooks — responsive breakpoints, mobile keyboard handling,
 * scroll management and body-scroll locking.
 *
 * These replace four slightly different (and partly broken) copies that lived
 * in ApplicantChatView, ApplicantMessagesView, ApplicantReferrerMessagesView
 * and EmployerMessagesPage.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/* ─── Breakpoints ─────────────────────────────────────────────────────────── */

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent | MediaQueryList) => setMatches(event.matches);
    onChange(mql);
    // Safari < 14 only supports addListener.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange as (e: MediaQueryListEvent) => void);
      return () => mql.removeEventListener('change', onChange as (e: MediaQueryListEvent) => void);
    }
    mql.addListener(onChange as (e: MediaQueryListEvent) => void);
    return () => mql.removeListener(onChange as (e: MediaQueryListEvent) => void);
  }, [query]);

  return matches;
}

/** True from the `md` breakpoint up — keeps every surface on one definition. */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 768px)');
}

export function useIsTouchDevice(): boolean {
  return useMediaQuery('(pointer: coarse)');
}

export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/* ─── Mobile keyboard ─────────────────────────────────────────────────────── */

export interface KeyboardInsets {
  /** Pixels of viewport hidden by the on-screen keyboard. */
  keyboardHeight: number;
  keyboardOpen: boolean;
}

/**
 * Tracks the visual viewport so a sticky composer stays above the on-screen
 * keyboard on iOS and Android without the layout jumping.
 *
 * `enabled` is usually `isMobile && chatIsOpen` so desktop pays nothing.
 */
export function useKeyboardInsets(enabled = true): KeyboardInsets {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setKeyboardHeight(0);
      return;
    }
    const vv = window.visualViewport;
    if (!vv) return;

    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // layout viewport - visual viewport = space taken by the keyboard.
        const hidden = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        setKeyboardHeight(hidden > 90 ? Math.round(hidden) : 0);
      });
    };

    measure();
    vv.addEventListener('resize', measure);
    vv.addEventListener('scroll', measure);
    window.addEventListener('orientationchange', measure);

    return () => {
      cancelAnimationFrame(frame);
      vv.removeEventListener('resize', measure);
      vv.removeEventListener('scroll', measure);
      window.removeEventListener('orientationchange', measure);
      setKeyboardHeight(0);
    };
  }, [enabled]);

  return useMemo(
    () => ({ keyboardHeight, keyboardOpen: keyboardHeight > 0 }),
    [keyboardHeight]
  );
}

/* ─── Viewport height ─────────────────────────────────────────────────────── */

/**
 * Publishes the real visible viewport height as `--msg-vh` on <html>.
 *
 * `100vh` on mobile Safari/Chrome measures the viewport WITHOUT the collapsing
 * browser chrome, so a `100vh` overlay is taller than the screen and its
 * bottom row (the composer) sits below the fold. `100dvh` fixes this but is
 * unsupported on older iOS; this hook provides the fallback the CSS uses:
 *
 *     height: 100vh;                              // ancient fallback
 *     height: calc(var(--msg-vh, 1vh) * 100);     // this hook
 *     height: 100dvh;                             // modern browsers win
 */
export function useViewportHeight(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const apply = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--msg-vh', `${height / 100}px`);
    };

    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    window.visualViewport?.addEventListener('resize', apply);

    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
      window.visualViewport?.removeEventListener('resize', apply);
    };
  }, []);
}

/* ─── Body scroll lock ────────────────────────────────────────────────────── */

let lockCount = 0;
let savedScrollY = 0;

/**
 * Locks background scrolling while a fullscreen chat overlay is open, and
 * restores the exact scroll position on close. Reference-counted so nested
 * overlays cannot leave the body stuck.
 */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active || typeof document === 'undefined') return;

    if (lockCount === 0) {
      savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.classList.add('isats-chat-immersive');
    }
    lockCount += 1;

    return () => {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.body.classList.remove('isats-chat-immersive');
        window.scrollTo(0, savedScrollY);
      }
    };
  }, [active]);
}

/* ─── Auto scroll ─────────────────────────────────────────────────────────── */

export interface AutoScrollResult {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  bottomRef: React.RefObject<HTMLDivElement | null>;
  isAtBottom: boolean;
  /** True when new content arrived while the user was reading history. */
  hasNewBelow: boolean;
  scrollToBottom: (smooth?: boolean) => void;
  onScroll: () => void;
  /** Call before prepending older messages to preserve the viewport anchor. */
  captureScrollAnchor: () => void;
}

/**
 * Keeps a thread pinned to the newest message, but never yanks the viewport
 * away from someone reading older history. Also restores position when older
 * pages are prepended.
 */
export function useAutoScroll(
  messageCount: number,
  conversationKey: string,
  threshold = 140
): AutoScrollResult {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const atBottomRef = useRef(true);
  const prevCountRef = useRef(messageCount);
  const anchorRef = useRef<{ height: number; top: number } | null>(null);
  const initialisedKeyRef = useRef<string | null>(null);

  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasNewBelow, setHasNewBelow] = useState(false);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = scrollRef.current;
    atBottomRef.current = true;
    setIsAtBottom(true);
    setHasNewBelow(false);
    if (!el) {
      bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
      return;
    }
    // scrollTop is more reliable than scrollIntoView inside nested flex panes.
    if (smooth && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distance < threshold;
    atBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
    if (atBottom) setHasNewBelow(false);
  }, [threshold]);

  const captureScrollAnchor = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    anchorRef.current = { height: el.scrollHeight, top: el.scrollTop };
  }, []);

  // Jump to the newest message when a conversation is opened.
  useLayoutEffect(() => {
    if (!conversationKey) return;
    if (initialisedKeyRef.current === conversationKey) return;
    if (messageCount === 0) return;
    initialisedKeyRef.current = conversationKey;
    prevCountRef.current = messageCount;
    atBottomRef.current = true;
    setIsAtBottom(true);
    setHasNewBelow(false);
    // Two frames: one for layout, one for images/fonts settling.
    requestAnimationFrame(() => {
      scrollToBottom(false);
      requestAnimationFrame(() => scrollToBottom(false));
    });
  }, [conversationKey, messageCount, scrollToBottom]);

  // React to new messages / prepended history.
  useLayoutEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = messageCount;

    if (messageCount === prev) return;

    // Older messages were prepended — restore the reading position.
    if (anchorRef.current) {
      const el = scrollRef.current;
      const anchor = anchorRef.current;
      anchorRef.current = null;
      if (el) {
        requestAnimationFrame(() => {
          el.scrollTop = anchor.top + (el.scrollHeight - anchor.height);
        });
        return;
      }
    }

    if (messageCount > prev) {
      if (atBottomRef.current) {
        requestAnimationFrame(() => scrollToBottom(true));
      } else {
        setHasNewBelow(true);
      }
    }
  }, [messageCount, scrollToBottom]);

  return {
    scrollRef,
    bottomRef,
    isAtBottom,
    hasNewBelow,
    scrollToBottom,
    onScroll,
    captureScrollAnchor,
  };
}

/* ─── Progressive message windowing ───────────────────────────────────────── */

export interface MessageWindow<T> {
  visible: T[];
  hasMore: boolean;
  hiddenCount: number;
  loadMore: () => void;
  reset: () => void;
}

/**
 * Lazy-renders long threads: only the newest `pageSize` messages hit the DOM,
 * older pages load on demand (button or scroll-to-top). Cheap alternative to a
 * virtualiser that keeps native scroll-anchoring and text search working.
 */
export function useMessageWindow<T>(
  items: T[],
  conversationKey: string,
  pageSize = 40
): MessageWindow<T> {
  const [limit, setLimit] = useState(pageSize);

  useEffect(() => {
    setLimit(pageSize);
  }, [conversationKey, pageSize]);

  const visible = useMemo(
    () => (items.length > limit ? items.slice(items.length - limit) : items),
    [items, limit]
  );

  const loadMore = useCallback(() => setLimit((l) => l + pageSize), [pageSize]);
  const reset = useCallback(() => setLimit(pageSize), [pageSize]);

  return {
    visible,
    hasMore: items.length > visible.length,
    hiddenCount: items.length - visible.length,
    loadMore,
    reset,
  };
}

/* ─── Escape / focus helpers ──────────────────────────────────────────────── */

export function useEscapeKey(onEscape: () => void, active = true): void {
  useEffect(() => {
    if (!active) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscape();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onEscape, active]);
}

/**
 * Traps Tab focus inside an overlay dialog and restores focus to the trigger
 * when it closes — required for `role="dialog" aria-modal="true"`.
 */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  active: boolean
): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const selector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const focusables = () =>
      Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [containerRef, active]);
}
